import { getAgent, Listener } from '@finos/fdc3';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { i18n } from '@terminal/utils/i18n/i18n';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { createFdc3IntentHandlers } from './fdc3IntentHandlers';

export const useFdc3 = () => {
  const navigate = useNavigate();

  const enabled = useFlags(state =>
    state.featureFlags.includes('REASON-2637-enable-fdc3')
  );
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let disposed = false;
    const listeners: Listener[] = [];

    // Handlers surface toasts via the global `t`; load the namespace here
    // (not via useTranslation) so a PoC-flagged bundle never suspends Root.
    i18n.loadNamespaces('fdc3');

    // Connect as soon as the flag is on: the Desktop Agent (e.g. Sail)
    // identifies this frame via the WCP hello, and delaying it until after
    // auth makes the app invisible to intent resolution. Handlers wait for
    // auth themselves before calling APIs.
    const connect = async () => {
      let agent;

      try {
        agent = await getAgent();
      } catch {
        return;
      }

      const handlers = createFdc3IntentHandlers(to => navigateRef.current(to));

      for (const [intent, handler] of Object.entries(handlers)) {
        const listener = await agent.addIntentListener(intent, handler);

        if (disposed) {
          listener.unsubscribe();
        } else {
          listeners.push(listener);
        }
      }
    };

    connect();

    return () => {
      disposed = true;
      listeners.forEach(listener => listener.unsubscribe());
    };
  }, [enabled]);
};
